import React from 'react'
import { motion } from 'framer-motion'
import { useAuth } from '../hooks/useAuth'
import { useDashboardData } from '../hooks/useDashboardData'
import { useLearningProgress } from '../hooks/useLearningProgress'
import Navigation from '../components/Navigation'
import DashboardHeader from '../components/dashboard/DashboardHeader'
import PortfolioOverview from '../components/dashboard/PortfolioOverview'
import SpendingSummary from '../components/dashboard/SpendingSummary'
import LearningProgress from '../components/dashboard/LearningProgress'
import BudgetProgress from '../components/dashboard/BudgetProgress'
import EnhancedLoadingSpinner from '../components/EnhancedLoadingSpinner'
import { BarChart3, TrendingUp, DollarSign, PieChart, Shield, Star } from 'lucide-react'

const DashboardPage: React.FC = () => {
  const { user, loading: authLoading, isGuest } = useAuth()
  const { data, loading, error } = useDashboardData()
  const { progress: learningProgress, achievements, loading: learningLoading } = useLearningProgress(user?.id)

  console.log('DashboardPage render:', { user, authLoading, loading, data, error, isGuest })

  // Show error state only for critical errors
  if (error && !isGuest && user) {
    return (
      <div className="min-h-screen bg-background-950 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-500 mb-4">
            Error loading dashboard
          </h1>
          <p className="text-background-300 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="luxury-button primary"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-950">
      {/* Navigation */}
      <Navigation />
      
      {/* Dashboard Header */}
      <DashboardHeader user={user} quickStats={data?.quickStats} />

      {/* Main Dashboard Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
        {/* Top Row - Key Metrics */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <PortfolioOverview portfolio={data?.portfolio} loading={loading} />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <SpendingSummary 
              transactions={data?.transactions} 
              budgets={data?.budgets}
              loading={loading}
            />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <LearningProgress 
              progress={learningProgress || data?.learningProgress || null} 
              achievements={achievements}
              loading={learningLoading}
            />
          </motion.div>
        </div>

        {/* Second Row - Budget */}
        <div className="grid grid-cols-1 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <BudgetProgress budgets={data?.budgets} loading={loading} />
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default DashboardPage
